import MainContainer from "@components/Container/MainContainer";
import MainWrapper from "@components/Container/MainWrapper";
import { HTTP_STATUS } from "@config/StatusConstants";
import { AdsImu2 } from "@elements/Advertisement/variant/AdsImu";
import { getGTMData } from "@helper/getMetaData";
import { Route } from "@route-types/Search/+types/SearchResultPage";
import ErrorPage from "@routes/StatusHandler/StatusHandler";
import {
  fetchContext,
  ResponseType,
  type TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { createMetaTags, type MetaTagsProps } from "@sphtech/web2-seo-schema";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import type { GADataProps } from "@typings/PageMetaData";
import { SearchPayloadResponseType } from "@typings/PagePayloadType";
import { ReactElement } from "react";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import NoResult from "./NoResult";
import { fetchSearchPageData } from "./Search.server";
import SearchInfiniteScroll from "./SearchInfiniteScroll";

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    SearchPayloadResponseType,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const metaData = dataLoaderResponse.payload.metaTags as MetaTagsProps;
    return createMetaTags(metaData);
  }
}

export default function SearchResultPage(): ReactElement {
  const dataLoaderResponse: TRouteWithRedirect<
    SearchPayloadResponseType,
    string
  > = useLoaderData();
  if (dataLoaderResponse.type) {
    return <ErrorPage response={dataLoaderResponse} />;
  }

  const searchedText = dataLoaderResponse.payload.searchedText;
  const resultCount = dataLoaderResponse.payload.resultCount;
  const searchedArticles = dataLoaderResponse.payload.data;
  const epaperCoverImageUrl =
    dataLoaderResponse.payload.epaperCoverImage.coverImageUri;

  const sectionName = "search";

  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;

  return (
    <MainContainer
      pageAdTargetValue={pageAdTargetValue}
      searchedText={searchedText}
      sectionName={sectionName}
      showEpaperBanner={true}
      epaperCoverImageUrl={epaperCoverImageUrl}
    >
      <MainWrapper className="flex flex-col lg:flex-row justify-between">
        <div className="w-full xmd:px-0 mt-4 xmd:mt-10">
          {searchedArticles.length === 0 ? (
            <NoResult />
          ) : (
            <SearchInfiniteScroll
              searchedArticles={searchedArticles}
              searchedText={searchedText}
              resultCount={resultCount}
              pageAdTargetValue={pageAdTargetValue}
            />
          )}
        </div>
        {/* Sidebar content */}
        <div className="lg:w-4/12 flex-col justify-items-center lg:min-h-[1800px] h-fit xmd:py-x1s xmd:pl-5 xmd:mt-5">
          <div className="xmd:my-5 w-full lg:min-h-[800px]">
            <AdsImu2
              sectionName={sectionName}
              pageAdTargetType={pageAdTargetValue}
              rootClassName="xmd:sticky lg:top-[160px] p-x1s justify-items-center w-full"
            />
          </div>
        </div>
      </MainWrapper>
    </MainContainer>
  );
}

export async function loader({ params }: Route.LoaderArgs) {
  const searchValue = params.searchValue;
  const data = await fetchSearchPageData(searchValue);

  if (data.statusCode !== HTTP_STATUS.SUCCESS) return { ...data };
  const gaData = data.payload.gaData;

  return {
    ...data,
    gtm: getGTMData(gaData as GADataProps),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<SearchPayloadResponseType, string>
  >;
}
